/**
 * SDMS Template Builder pickers — the Scientific Data field picker and the Link
 * Test dialog. Both are the shared <AppDialog> + <AppDataGrid> (standing rules 7/9).
 */
import { useEffect, useMemo, useState } from 'react';
import { AppDialog } from '../../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { useNotify } from '../../../components/notifications';
import {
  errorText, sdmsTemplateApi,
  type FieldOption, type TemplateTest, type TestMethodOption, type TestParameterOption,
} from './sdmsTemplateApi';

// ── Field picker ────────────────────────────────────────────────────────────

const FIELD_COLS: AppColumn<FieldOption>[] = [
  { field: 'fieldName', header: 'Field', width: 200 },
  { field: 'synonymEN', header: 'Caption', width: 200 },
  { field: 'resultType', header: 'Result Type', width: 140 },
  { field: 'dataType', header: 'Data Type', width: 110 },
  { field: 'description', header: 'Description', width: 260 },
];

export function FieldPickerDialog({ open, title, fields, exclude, multi, defaultResultType, onPick, onClose }: {
  open: boolean;
  title: string;
  fields: FieldOption[];
  /** field ids already used (hidden from the list) */
  exclude?: ReadonlySet<number>;
  multi?: boolean;
  defaultResultType?: string;
  onPick: (picked: FieldOption[]) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [resultType, setResultType] = useState(defaultResultType ?? 'RawDataTable');
  const [selected, setSelected] = useState<Set<string | number>>(new Set());
  useEffect(() => { if (open) { setSearch(''); setSelected(new Set()); } }, [open]);

  const types = useMemo(() => [...new Set(fields.map(f => f.resultType ?? ''))].filter(Boolean).sort(), [fields]);
  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return fields.filter(f =>
      (!resultType || f.resultType === resultType)
      && !exclude?.has(f.id)
      && (!q || `${f.fieldName} ${f.synonymEN} ${f.description}`.toLowerCase().includes(q)));
  }, [fields, search, resultType, exclude]);

  const confirm = () => {
    const picked = fields.filter(f => selected.has(f.id));
    if (picked.length) onPick(picked);
  };

  return (
    <AppDialog open={open} title={title} icon="ti-list-search" width={860} onClose={onClose}
      footer={<>
        <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="ca-btn ca-btn-solid" disabled={selected.size === 0} onClick={confirm}>OK</button>
      </>}>
      <div className="stb-picker-bar">
        <div className="list-search">
          <span className="tb-i">🔍</span>
          <input type="text" placeholder="Search field, caption, description…" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="sd-input stb-picker-type" value={resultType} onChange={e => setResultType(e.target.value)} aria-label="Result type">
          <option value="">All result types</option>
          {types.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>
      <div className="stb-picker-grid">
        <AppDataGrid<FieldOption>
          rows={rows} columns={FIELD_COLS} getRowId={r => r.id} defaultPageSize={10}
          selectable selectedIds={selected}
          onSelectionChange={ids => setSelected(multi ? ids : new Set([...ids].slice(-1)))}
          onRowClick={r => setSelected(prev => {
            if (!multi) return new Set([r.id]);
            const next = new Set(prev);
            if (next.has(r.id)) next.delete(r.id); else next.add(r.id);
            return next;
          })}
          onRowDoubleClick={r => onPick([r])}
          hideHeaderTools emptyText="No fields match." />
      </div>
    </AppDialog>
  );
}

// ── Link Test ───────────────────────────────────────────────────────────────

const TEST_COLS: AppColumn<TestMethodOption>[] = [
  { field: 'testName', header: 'Test', width: 220 },
  { field: 'methodName', header: 'Method', width: 160 },
  { field: 'matrixName', header: 'Matrix', width: 130 },
  { field: 'parameterCount', header: 'Parameters', type: 'number', width: 100 },
];
const PARAM_COLS: AppColumn<TestParameterOption>[] = [
  { field: 'parameterName', header: 'Parameter', width: 260 },
  { field: 'qcTypeName', header: 'QC Type', width: 160 },
];

export interface LinkedTestPick { test: TestMethodOption; parameters: TestParameterOption[] }

export function LinkTestDialog({ open, tests, linkedTestIds, onPick, onClose }: {
  open: boolean;
  tests: TestMethodOption[];
  linkedTestIds: ReadonlySet<string>;
  onPick: (pick: LinkedTestPick) => void;
  onClose: () => void;
}) {
  const notify = useNotify();
  const [search, setSearch] = useState('');
  const [test, setTest] = useState<TestMethodOption | null>(null);
  const [params, setParams] = useState<TestParameterOption[]>([]);
  const [chosen, setChosen] = useState<Set<string | number>>(new Set());
  const [loading, setLoading] = useState(false);
  useEffect(() => { if (open) { setSearch(''); setTest(null); setParams([]); setChosen(new Set()); } }, [open]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tests.filter(t => !linkedTestIds.has(t.id.toUpperCase())
      && (!q || `${t.testName} ${t.methodName} ${t.matrixName}`.toLowerCase().includes(q)));
  }, [tests, search, linkedTestIds]);

  const choose = async (t: TestMethodOption) => {
    setTest(t);
    setLoading(true);
    try {
      const p = await sdmsTemplateApi.testParameters(t.id);
      setParams(p);
      setChosen(new Set(p.map(x => x.testParameterID)));        // every parameter, as legacy links them
    } catch (e) {
      notify.error(errorText(e, 'Could not load the test parameters.'));
    } finally {
      setLoading(false);
    }
  };

  const confirm = () => {
    if (!test) return;
    onPick({ test, parameters: params.filter(p => chosen.has(p.testParameterID)) });
  };

  return (
    <AppDialog open={open} title={test ? `Link Test — ${test.testName ?? ''}` : 'Link Test'} icon="ti-link" width={820} onClose={onClose}
      footer={<>
        {test && <button type="button" className="ca-btn" onClick={() => setTest(null)}>Back</button>}
        <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="ca-btn ca-btn-solid" disabled={!test || chosen.size === 0} onClick={confirm}>OK</button>
      </>}>
      {!test ? (
        <>
          <div className="stb-picker-bar">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input type="text" placeholder="Search test, method, matrix…" value={search} onChange={e => setSearch(e.target.value)} />
            </div>
          </div>
          <div className="stb-picker-grid">
            <AppDataGrid<TestMethodOption>
              rows={rows} columns={TEST_COLS} getRowId={r => r.id} defaultPageSize={10}
              onRowClick={r => void choose(r)} hideHeaderTools
              emptyText="No tests match (tests already linked are hidden)." />
          </div>
        </>
      ) : (
        <>
          <p className="stb-hint">
            {test.methodName} · {test.matrixName} — choose the parameters this template serves.
            SDMS Result Entry offers the template for the test through these rows.
          </p>
          <div className="stb-picker-grid">
            <AppDataGrid<TestParameterOption>
              rows={params} columns={PARAM_COLS} getRowId={r => r.testParameterID} defaultPageSize={10}
              selectable selectedIds={chosen} onSelectionChange={setChosen} loading={loading}
              hideHeaderTools emptyText="This test has no live parameters." />
          </div>
        </>
      )}
    </AppDialog>
  );
}

// ── Test (legacy three-list picker) ─────────────────────────────────────────

interface SelectedTest { id: string; testName: string; methodName: string; matrixName: string }

const AVAILABLE_COLS: AppColumn<TestMethodOption>[] = [
  { field: 'matrixName', header: 'Matrix', width: 90 },
  { field: 'testName', header: 'Test', width: 170 },
  { field: 'methodName', header: 'MethodName', width: 130 },
];
const SELECTED_COLS: AppColumn<SelectedTest>[] = [
  { field: 'matrixName', header: 'Matrix', width: 90 },
  { field: 'testName', header: 'Test', width: 170 },
  { field: 'methodName', header: 'Method', width: 130 },
];
const SELECTED_PARAM_COLS: AppColumn<TemplateTest>[] = [
  { field: 'parameterName', header: 'Parameter', width: 240,
    value: r => [r.parameterName, r.qcTypeName].filter(Boolean).join(' / '),
    text: r => [r.parameterName, r.qcTypeName].filter(Boolean).join(' / ') },
];

/**
 * The legacy "Test" popup of the SDMS Template Builder detail view: Available Test ·
 * Selected Test · Selected Parameter, with Add · Remove · OK. Opened by clicking the Test field
 * on the legacy General Info tab.
 *
 * It edits a COPY of the template's linked tests and hands the whole set back on OK, so closing
 * it any other way changes nothing. Add links the ticked tests with EVERY live parameter (as legacy
 * links them); Remove unlinks the ticked selected tests.
 */
export function LegacyTestDialog({ open, tests, linked, onApply, onClose }: {
  open: boolean;
  /** every test that can be linked */
  tests: TestMethodOption[];
  /** the template's linked tests, one row per parameter */
  linked: TemplateTest[];
  onApply: (rows: TemplateTest[]) => void;
  onClose: () => void;
}) {
  const notify = useNotify();
  const [work, setWork] = useState<TemplateTest[]>([]);
  const [availSearch, setAvailSearch] = useState('');
  const [selSearch, setSelSearch] = useState('');
  const [paramSearch, setParamSearch] = useState('');
  const [tickAvail, setTickAvail] = useState<Set<string | number>>(new Set());
  const [tickSel, setTickSel] = useState<Set<string | number>>(new Set());
  const [focus, setFocus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setWork(linked); setAvailSearch(''); setSelSearch(''); setParamSearch('');
    setTickAvail(new Set()); setTickSel(new Set()); setFocus(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const key = (id: string) => id.toUpperCase();
  const has = (q: string, ...parts: (string | null)[]) => !q || parts.join(' ').toLowerCase().includes(q);

  const selected = useMemo<SelectedTest[]>(() => {
    const m = new Map<string, SelectedTest>();
    for (const t of work) {
      const k = key(t.testMethodID);
      if (!m.has(k)) m.set(k, { id: k, testName: t.testName ?? '', methodName: t.methodName ?? '', matrixName: t.matrixName ?? '' });
    }
    return [...m.values()];
  }, [work]);

  const available = useMemo(() => {
    const taken = new Set(selected.map(s => s.id));
    const q = availSearch.trim().toLowerCase();
    return tests.filter(t => !taken.has(key(t.id)) && has(q, t.matrixName, t.testName, t.methodName));
  }, [tests, selected, availSearch]);

  const selectedShown = useMemo(() => {
    const q = selSearch.trim().toLowerCase();
    return selected.filter(s => has(q, s.matrixName, s.testName, s.methodName));
  }, [selected, selSearch]);

  // Parameters of the test clicked in Selected Test; with none clicked, of every selected test.
  const params = useMemo(() => {
    const q = paramSearch.trim().toLowerCase();
    return work.filter(t => (!focus || key(t.testMethodID) === focus) && has(q, t.parameterName, t.qcTypeName));
  }, [work, focus, paramSearch]);

  const add = async () => {
    const picks = tests.filter(t => tickAvail.has(t.id));
    if (picks.length === 0) return;
    setBusy(true);
    try {
      const added: TemplateTest[] = [];
      for (const t of picks) {
        const ps = await sdmsTemplateApi.testParameters(t.id);
        for (const p of ps) {
          added.push({
            uqID: 0, testMethodID: t.id, testParameterID: p.testParameterID, testName: t.testName,
            methodName: t.methodName, matrixName: t.matrixName, parameterName: p.parameterName,
            qcTypeName: p.qcTypeName, parameter: 'All', supportingParameter: null,
          });
        }
        if (ps.length === 0) notify.warning(`${t.testName ?? 'This test'} has no live parameters, so it was not added.`);
      }
      setWork(w => [...w, ...added]);
      setTickAvail(new Set());
    } catch (e) {
      notify.error(errorText(e, 'Could not load the test parameters.'));
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    if (tickSel.size === 0) return;
    setWork(w => w.filter(t => !tickSel.has(key(t.testMethodID))));
    if (focus && tickSel.has(focus)) setFocus(null);
    setTickSel(new Set());
  };

  const search = (value: string, set: (v: string) => void) => (
    <div className="list-search stb-test3-search">
      <span className="tb-i">🔍</span>
      <input type="text" placeholder="Enter text to search…" value={value} onChange={e => set(e.target.value)} />
    </div>
  );

  return (
    <AppDialog open={open} title="Test" icon="ti-flask" width={1180} onClose={onClose}
      footer={<>
        <button type="button" className="ca-btn" disabled={busy || tickAvail.size === 0} onClick={() => void add()}>Add</button>
        <button type="button" className="ca-btn" disabled={busy || tickSel.size === 0} onClick={remove}>Remove</button>
        <button type="button" className="ca-btn ca-btn-solid" disabled={busy} onClick={() => onApply(work)}>OK</button>
      </>}>
      <div className="stb-test3" data-unsaved-ignore>
        <section>
          <div className="stb-sub">Available Test</div>
          {search(availSearch, setAvailSearch)}
          <AppDataGrid<TestMethodOption> rows={available} columns={AVAILABLE_COLS} getRowId={r => r.id}
            selectable selectedIds={tickAvail} onSelectionChange={setTickAvail} loading={busy}
            defaultPageSize={10} hideHeaderTools emptyText="No data to display" />
        </section>
        <section>
          <div className="stb-sub">Selected Test</div>
          {search(selSearch, setSelSearch)}
          <AppDataGrid<SelectedTest> rows={selectedShown} columns={SELECTED_COLS} getRowId={r => r.id}
            selectable selectedIds={tickSel} onSelectionChange={setTickSel}
            onRowClick={r => setFocus(f => (f === r.id ? null : r.id))} selectedRowId={focus}
            defaultPageSize={10} hideHeaderTools emptyText="No data to display" />
        </section>
        <section>
          <div className="stb-sub">Selected Parameter</div>
          {search(paramSearch, setParamSearch)}
          <AppDataGrid<TemplateTest> rows={params} columns={SELECTED_PARAM_COLS}
            getRowId={r => `${r.testMethodID}|${r.testParameterID}`}
            defaultPageSize={10} hideHeaderTools emptyText="No data to display" />
        </section>
      </div>
    </AppDialog>
  );
}

// ── FieldName (legacy Parsing Data cell popup) ──────────────────────────────

const FIELD_NAME_COLS: AppColumn<FieldOption>[] = [{ field: 'fieldName', header: 'FieldName', width: 520 }];

/**
 * The legacy "FieldName" popup of the Parsing Data grid: the Raw Data fields in one FieldName
 * column, with a Search box, a tick per row and OK · Cancel. Opened by clicking a row's
 * FieldName cell; the ticked field becomes that row's field.
 *
 * One parsing row maps ONE field, so the tick is single — ticking another field moves it.
 * Double-clicking a field picks it straight away.
 */
export function LegacyFieldNameDialog({ open, fields, currentId, onPick, onClose }: {
  open: boolean;
  fields: FieldOption[];
  /** the field the row already has, pre-ticked */
  currentId?: number;
  onPick: (field: FieldOption) => void;
  onClose: () => void;
}) {
  const [search, setSearch] = useState('');
  const [ticked, setTicked] = useState<Set<string | number>>(new Set());
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setTicked(currentId && currentId > 0 ? new Set([currentId]) : new Set());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const rows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return fields.filter(f => f.resultType === 'RawDataTable' && f.fieldName && (!q || f.fieldName.toLowerCase().includes(q)));
  }, [fields, search]);

  const chosen = fields.find(f => ticked.has(f.id)) ?? null;
  // Keep the tick single: the newly ticked field replaces the previous one.
  const onTick = (next: Set<string | number>) => {
    const added = [...next].filter(id => !ticked.has(id));
    setTicked(added.length ? new Set([added[added.length - 1]!]) : next.size <= 1 ? next : new Set());
  };

  return (
    <AppDialog open={open} title="FieldName" icon="ti-list-search" width={720} onClose={onClose}
      footer={<>
        <button type="button" className="ca-btn ca-btn-solid" disabled={!chosen} onClick={() => { if (chosen) onPick(chosen); }}>OK</button>
        <button type="button" className="ca-btn" onClick={onClose}>Cancel</button>
      </>}>
      <div className="stb-picker-bar stb-fieldname-bar" data-unsaved-ignore>
        <div className="list-search">
          <span className="tb-i">🔍</span>
          <input type="text" placeholder="Text to search…" aria-label="Search" value={search} onChange={e => setSearch(e.target.value)} />
        </div>
      </div>
      <div className="stb-picker-grid">
        <AppDataGrid<FieldOption> rows={rows} columns={FIELD_NAME_COLS} getRowId={r => r.id}
          selectable selectedIds={ticked} onSelectionChange={onTick}
          onRowClick={r => setTicked(new Set([r.id]))} onRowDoubleClick={r => onPick(r)}
          defaultPageSize={20} hideHeaderTools emptyText="No data to display" />
      </div>
    </AppDialog>
  );
}
